import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  Grid,
  TableContainer,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Paper,
  Tooltip,
  Badge,
  IconButton,
} from "@mui/material";
import PropTypes from "prop-types";
import BackdropComponent from "../../../Spinner/Spinner";
import TextLabel from "components/Common/TextLabel/TextLabel";
import classes from "../../OMTBulkEdit/Common.module.css";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import { CustomButton } from "../../../../components/RosterConfig/RosterConfigStyle";
import SnackBar from "../../../../components/SnackBar/SnackBar";

import {
  getOMTOperatingSystem,
  saveOMTOperatingSystem,
} from "../../../../services/userServices";
import { AttachFile } from "@mui/icons-material";
import OMTAttachments from "../OMTAttachments";
import { red } from "@mui/material/colors";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useColumnFilter } from "../useColumnFilter";
import ColumnFilterPopover from "../ColumnFilterPopover";

const isLockedByHardware = (row) => {
  const reason = row.HardwareFailureReason;
  if (!reason) return false;
  if (reason.startsWith("Pre-pass")) return false;
  return true;
};

const OperatingSystem = forwardRef(function OperatingSystem(props, ref) {
  const {
    rosterId,
    asdLeadOracleId,
    taskTypeId,
    scheduleType,
    editAccess,
    agentOracleId,
    onDirtyChange,
    noRosterIdClassName,
    isUploadMode,
    isNoRosterIdSearch,
  } = props;

  const [loading, setLoader] = useState(false);
  const [originalOperatingSystemData, setOriginalOperatingSystemData] =
    useState([]);
  const [order, setOrder] = useState("ASC");
  const [OperatingSystemData, setOperatingSystemData] = useState([]);
  const [openAttachmentDialog, setOpenAttachmentDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const isFirstRender = useRef(true);

  const handleAttachmentOpen = (row) => {
    setOpenAttachmentDialog(true);
    setSelectedRow(row);
  };

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const snackReset = () => setSnack({ open: false, message: "", severity: "" });

  const isDirty =
    JSON.stringify(OperatingSystemData) !==
    JSON.stringify(originalOperatingSystemData);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]);

  useImperativeHandle(ref, () => ({ save: () => handleSave() }));

  useEffect(() => {
    let cancelled = false;

    const loadOperatingSystemData = async () => {
      if (
        (rosterId !== null && rosterId !== "") ||
        (noRosterIdClassName !== null && noRosterIdClassName !== "") ||
        (agentOracleId !== null && agentOracleId !== "" && !noRosterIdClassName)
      ) {
        if (!cancelled) setOperatingSystemData([]);
        try {
          await fetchOperatingSystemData(
            rosterId,
            asdLeadOracleId,
            taskTypeId,
            scheduleType,
            agentOracleId,
            noRosterIdClassName,
            isNoRosterIdSearch,
          );
        } catch (error) {
          if (!cancelled)
            console.error("Failed to fetch operating system data:", error);
        }
      } else {
        setOperatingSystemData([]);
      }
    };

    loadOperatingSystemData();

    return () => {
      cancelled = true;
    };
  }, [rosterId, agentOracleId, noRosterIdClassName]);

  useEffect(() => {
    let cancelled = false;

    const refreshAfterAttachment = async () => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return; // Skip first render
      }

      if (openAttachmentDialog === false) {
        try {
          await fetchOperatingSystemData(
            rosterId,
            asdLeadOracleId,
            taskTypeId,
            scheduleType,
            agentOracleId,
            noRosterIdClassName,
            isNoRosterIdSearch,
          );
        } catch (error) {
          if (!cancelled)
            console.error("Failed to refresh operating system data:", error);
        }
      }
    };
    refreshAfterAttachment();

    return () => {
      cancelled = true;
    };
  }, [openAttachmentDialog]);

  const fetchOperatingSystemData = async (
    rosterId,
    asdLeadOracleId,
    taskTypeId,
    scheduleType,
    agentOracleId,
    noRosterIdClassName,
    isNoRosterIdSearch,
  ) => {
    setLoader(true);
    try {
      const response = await getOMTOperatingSystem(
        rosterId,
        asdLeadOracleId,
        taskTypeId,
        scheduleType,
        agentOracleId,
        noRosterIdClassName,
        isNoRosterIdSearch,
      );

      if (response?.data?.message === "Success") {
        setOperatingSystemData(response.data.data);
        setOriginalOperatingSystemData(
          JSON.parse(JSON.stringify(response.data.data)),
        );
      } else {
        setOperatingSystemData([]);
        setOriginalOperatingSystemData([]);
      }
    } catch (error) {
      const is404 =
        error?.response?.status === 404 || error?.statusCode === 404;
      if (is404) {
        setOperatingSystemData([]);
        setOriginalOperatingSystemData([]);
        if (rosterId || noRosterIdClassName) {
          setSnack({
            open: true,
            message: "No OS records found for the selected filters.",
            severity: "info",
          });
        }
      } else {
        setSnack({
          open: true,
          message: "Error fetching OS data",
          severity: "error",
        });
      }
    }
    setLoader(false);
  };

  const sortByCol = (data, col, direction) =>
    [...data].sort((a, b) => {
      if (typeof a[col] === "string" && typeof b[col] === "string") {
        return direction === "ASC"
          ? a[col].toLowerCase() > b[col].toLowerCase()
            ? 1
            : -1
          : a[col].toLowerCase() < b[col].toLowerCase()
            ? 1
            : -1;
      }
      return direction === "ASC"
        ? a[col] > b[col]
          ? 1
          : -1
        : a[col] < b[col]
          ? 1
          : -1;
    });

  const sorting = (col) => {
    if (order === "ASC") {
      setOperatingSystemData(sortByCol(OperatingSystemData, col, "ASC"));
      setOriginalOperatingSystemData(
        sortByCol(originalOperatingSystemData, col, "ASC"),
      );
      setOrder("DSC");
    } else if (order === "DSC") {
      setOperatingSystemData(sortByCol(OperatingSystemData, col, "DSC"));
      setOriginalOperatingSystemData(
        sortByCol(originalOperatingSystemData, col, "DSC"),
      );
      setOrder("ASC");
    }
  };

  const handleChangeChecked = (event, rowIndex) => {
    let trainee = OperatingSystemData.map((item, index) => {
      if (index === rowIndex) {
        return {
          ...item,
          [event.target.name]: event.target.checked,
        };
      } else {
        return item;
      }
    });

    setOperatingSystemData(trainee);
  };

  const handleReset = async () => {
    fetchOperatingSystemData(
      rosterId,
      asdLeadOracleId,
      taskTypeId,
      scheduleType,
      agentOracleId,
      noRosterIdClassName,
      isNoRosterIdSearch,
    );
  };

  const handleSave = async () => {
    const hasChanges =
      JSON.stringify(OperatingSystemData) !==
      JSON.stringify(originalOperatingSystemData);

    if (!hasChanges) {
      setSnack({
        open: true,
        message: "No changes to Save.",
        severity: "info",
      });
      return true;
    }
    setLoader(true);
    try {
      const data = createData();
      if (data.length !== OperatingSystemData.length) return false;
      const response = await saveOMTOperatingSystem(
        data,
        localStorage.getItem("User"),
      );

      if (response?.message === "Success") {
        setSnack({
          open: true,
          message: "All changes have been saved Successfully.",
          severity: "success",
        });
        await fetchOperatingSystemData(
          rosterId,
          asdLeadOracleId,
          taskTypeId,
          scheduleType,
          agentOracleId,
          noRosterIdClassName,
          isNoRosterIdSearch,
        );
        return true;
      }
      setSnack({ open: true, message: "Failed to Save..", severity: "error" });
      return false;
    } catch {
      setSnack({
        open: true,
        message: "Error saving OperatingSystem..",
        severity: "error",
      });
      return false;
    } finally {
      setLoader(false);
    }
  };

  const createData = () => {
    const booleanFields = [
      "Bitlocker",
      "HostOSNoBootDuringTCI",
      "HostOSNoBootPreTCI",
      "NotEnoughUSBPorts",
      "SlowPerformance",
    ];

    return OperatingSystemData.map((record) => ({
      OMTOperatingSystemId: record.OMTOperatingSystemId || 0,
      OMTClassDetailsId: record.OMTClassDetailsId,
      ClassRosterId: record.ClassRosterId,
      OracleId: record.OracleId,
      EmployeeName: record.EmployeeName,
      TraineeStatus: record.TraineeStatus,
      OMTScheduleStatusType: record.ScheduleType || "",
      ...Object.fromEntries(
        booleanFields.map((field) => [field, record[field] || false]),
      ),
    }));
  };

  const traineeStatusFilter = useColumnFilter(
    OperatingSystemData,
    "TraineeStatus",
  );

  const FilteredOperatingSystemData = useMemo(() => {
    return OperatingSystemData.map((row, originalIndex) => ({
      row,
      originalIndex,
    })).filter(({ row }) => traineeStatusFilter.matches(row));
  }, [OperatingSystemData, traineeStatusFilter.selectedValues]);

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Box>
        <Paper sx={{ padding: 2 }}>
          <TableContainer className={classes.container}>
            <Table stickyHeader size="small" aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{ backgroundColor: "white" }}
                    onClick={() =>
                      sorting(
                        isUploadMode ? "NoRosterIdClassName" : "ClassRosterId",
                      )
                    }
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={isUploadMode ? "Class Name" : "RID"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        style={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    style={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                    onClick={() => sorting("Name")}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Name"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        style={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                    onClick={() => sorting("Schedule Type")}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Schedule Type"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                    onClick={() => sorting("Schedule Status")}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Schedule Status"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Trainee Status"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <IconButton
                        onClick={traineeStatusFilter.openFilter}
                        size="small"
                        color={
                          traineeStatusFilter.selectedValues.length
                            ? "primary"
                            : "default"
                        }
                      >
                        <FilterListIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell style={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Bitlocker"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell
                    style={{ backgroundColor: "white", minWidth: "150px" }}
                  >
                    <TextLabel
                      text={"Host OS No Boot during TCI"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell
                    style={{ backgroundColor: "white", minWidth: "150px" }}
                  >
                    <TextLabel
                      text={"Host OS No Boot Pre-TCI"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell
                    style={{ backgroundColor: "white", minWidth: "150px" }}
                  >
                    <TextLabel
                      text={"Not Enough USB Ports"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell style={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Slow Performance"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {FilteredOperatingSystemData.map(({ row, originalIndex }) => {
                  const rowKey = `${row.OracleId}-${originalIndex}`;
                  const isDisabled =
                    row.AttendanceNoShow === true ||
                    row.AttendanceISPOutage === true || // disable if no show or ISP outage in attendance tab.
                    row.CredentialsPasswordNotWorking === true || // disable if Password Not Working in AD Credential tab.
                    row.CredentialsWindowsLoginFailure === true || // disable if Windows Login Failure in AD Credential tab.
                    row.NetworkReasonLock === 1 || // disable if On Down/Up speed fail, Wi-Fi – LOB,Packet Loss / Latency,ISP and Geolocation Outside Country or SIP ALG Enabled checked in Network tab.
                    isLockedByHardware(row); // disable if hardware issue
                  return (
                    <TableRow key={rowKey}>
                      <TableCell align="center">
                        <Grid container>
                          <Grid item xs={12}>
                            <TextLabel
                              text={
                                noRosterIdClassName
                                  ? row.NoRosterIdClassName
                                  : row.ClassRosterId
                              }
                              fontSize={14}
                              subHeading
                            />
                          </Grid>
                          <Grid item xs={12} sx={{ mt: 1 }}>
                            <Tooltip title="Attach File">
                              <Badge
                                badgeContent={row?.AttachmentCount || 0}
                                color="primary"
                                sx={{
                                  "& .MuiBadge-badge": {
                                    color: "#ffffff",
                                    backgroundColor: red[700],
                                  },
                                }}
                              >
                                <IconButton
                                  onClick={() => handleAttachmentOpen(row)}
                                  size="small"
                                  disableRipple
                                  disabled={isDisabled}
                                  sx={{
                                    backgroundColor: "#3299ff",
                                    borderRadius: 1,
                                  }}
                                  aria-label="attach file"
                                >
                                  <AttachFile
                                    fontSize="small"
                                    sx={{ color: "#ffffff" }}
                                  />
                                </IconButton>
                              </Badge>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </TableCell>
                      <TableCell>{row.EmployeeName}</TableCell>
                      <TableCell>
                        <TextLabel text={row.ScheduleType} description />
                      </TableCell>
                      <TableCell>
                        <TextLabel text={row.ScheduleStatus} description />
                      </TableCell>
                      <TableCell>
                        <TextLabel
                          text={row.TraineeStatus}
                          description
                        ></TextLabel>
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled}
                          name="Bitlocker"
                          checked={row.Bitlocker}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled}
                          name="HostOSNoBootDuringTCI"
                          checked={row.HostOSNoBootDuringTCI}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled}
                          name="HostOSNoBootPreTCI"
                          checked={row.HostOSNoBootPreTCI}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled}
                          name="NotEnoughUSBPorts"
                          checked={row.NotEnoughUSBPorts}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled}
                          name="SlowPerformance"
                          checked={row.SlowPerformance}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <ColumnFilterPopover
            anchorEl={traineeStatusFilter.anchorEl}
            onClose={traineeStatusFilter.closeFilter}
            options={traineeStatusFilter.uniqueValues}
            selectedValues={traineeStatusFilter.selectedValues}
            onToggle={traineeStatusFilter.toggleValue}
            onClear={traineeStatusFilter.clearFilter}
          />
          <Box className={classes.buttonBox}>
            <CustomButton
              variant="contained"
              className={classes.buttonSave}
              size="small"
              onClick={handleSave}
              disabled={!editAccess}
            >
              Save
            </CustomButton>
            <CustomButton
              variant="outlined"
              className={classes.buttonReset}
              size="small"
              onClick={handleReset}
              disabled={!editAccess}
            >
              Reset
            </CustomButton>
          </Box>
        </Paper>
      </Box>
      <SnackBar snack={snack} snackReset={snackReset} />
      {openAttachmentDialog && (
        <OMTAttachments
          openAttachmentDialog={openAttachmentDialog}
          row={selectedRow}
          category="OS"
          onClose={() => setOpenAttachmentDialog(false)}
          editAccess={editAccess}
        />
      )}
    </>
  );
});

OperatingSystem.propTypes = {
  rosterId: PropTypes.string,
  asdLeadOracleId: PropTypes.string,
  taskTypeId: PropTypes.string,
  scheduleType: PropTypes.string,
  editAccess: PropTypes.bool,
  agentOracleId: PropTypes.string,
  onDirtyChange: PropTypes.func,
};

export default OperatingSystem;
